import { For, Show, createMemo } from "solid-js"
import type { ScheduledTasksController } from "./scheduled-tasks-controller"
import type { ScheduledTask, ExecutionRecord, TaskFormState } from "./scheduled-tasks-types"
import { StIcon, type StIconName } from "./scheduled-tasks-icons"
import "./scheduled-tasks.css"

// ===== 时间辅助 =====
const WEEKDAY = ["日", "一", "二", "三", "四", "五", "六"]

function pad(n: number) {
  return String(n).padStart(2, "0")
}

function startOfDay(d: Date) {
  const c = new Date(d)
  c.setHours(0, 0, 0, 0)
  return c
}

function sameDay(a: Date, b: Date) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

/** 明天 / 今天 / 10 月 21 日 */
function dayLabel(d: Date): string {
  const today = startOfDay(new Date())
  const target = startOfDay(d)
  const diff = Math.round((target.getTime() - today.getTime()) / 86400000)
  if (diff === 0) return "今天"
  if (diff === 1) return "明天"
  if (diff === -1) return "昨天"
  return `${d.getMonth() + 1} 月 ${d.getDate()} 日`
}

/** 相对时间：23 分钟后 / 3 天前 */
function relativeTime(ts?: number): string {
  if (!ts) return "未执行过"
  const diff = ts - Date.now()
  const abs = Math.abs(diff)
  const suffix = diff >= 0 ? "后" : "前"
  const minutes = Math.round(abs / 60000)
  if (minutes < 1) return diff >= 0 ? "即将执行" : "刚刚"
  if (minutes < 60) return `${minutes} 分钟${suffix}`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours} 小时${suffix}`
  const days = Math.round(hours / 24)
  if (days <= 60) return `${days} 天${suffix}`
  return `${Math.round(days / 30)} 个月${suffix}`
}

/** 单条任务在指定日期是否会被触发 */
function runsOn(task: ScheduledTask, day: Date): boolean {
  switch (task.schedule_type) {
    case "daily":
      return true
    case "weekly":
      return day.getDay() === (task.schedule_weekday ?? 1)
    case "monthly":
      return day.getDate() === (task.schedule_month_day ?? 1)
    case "once":
      return task.scheduled_at ? sameDay(new Date(task.scheduled_at), day) : false
  }
}

const CADENCE_ICON: Record<ScheduledTask["schedule_type"], StIconName> = {
  once: "bolt",
  daily: "clock",
  weekly: "week",
  monthly: "month",
}

// ===== 小部件 =====
function Switch(props: { on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      class="st-switch"
      classList={{ "st-switch--on": props.on }}
      role="switch"
      aria-checked={props.on}
      title={props.on ? "运行中，点击暂停" : "已暂停，点击启用"}
      onClick={(e) => {
        e.stopPropagation()
        props.onToggle()
      }}
    >
      <span class="st-switch-knob" />
    </button>
  )
}

function StatusBadge(props: { status: ExecutionRecord["status"]; label: string }) {
  return (
    <span class="st-badge" data-status={props.status}>
      <i class="st-badge-dot" />
      {props.label}
    </span>
  )
}

// ===== 空态 =====
function EmptyState(props: { icon: StIconName; title: string; desc: string; action?: () => void }) {
  return (
    <div class="st-empty">
      <div class="st-empty-icon">
        <StIcon name={props.icon} size={20} />
      </div>
      <p class="st-empty-title">{props.title}</p>
      <p class="st-empty-desc">{props.desc}</p>
      <Show when={props.action}>
        <button class="st-btn-primary st-btn-primary--sm" onClick={() => props.action?.()}>
          <StIcon name="plus" size={13} />
          新建任务
        </button>
      </Show>
    </div>
  )
}

// ===== 任务行 =====
function TaskRow(props: { c: ScheduledTasksController; task: ScheduledTask }) {
  const c = props.c
  const t = props.task
  const active = () => t.status === "active"

  return (
    <article
      class="st-task"
      classList={{ "st-task--paused": !active() }}
      onClick={() => c.openEdit(t)}
      onKeyDown={(e) => {
        // 只处理卡片自身获得焦点的情况，内部按钮的键盘事件不往上冒成"打开编辑"
        if (e.target !== e.currentTarget) return
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault()
          c.openEdit(t)
        }
      }}
      role="button"
      tabindex="0"
    >
      <div class="st-task-icon" data-cadence={t.schedule_type}>
        <StIcon name={CADENCE_ICON[t.schedule_type]} size={17} />
      </div>

      <div class="st-task-main">
        <div class="st-task-title-row">
          <span class="st-task-name">{t.name}</span>
          <span class="st-task-cadence">{c.scheduleLabel(t)}</span>
        </div>
        <p class="st-task-prompt">{t.prompt}</p>
        <div class="st-task-meta">
          <span class="st-task-meta-item">
            <StIcon name="history" size={12} />
            上次 {c.formatTime(t.last_run_at)}
          </span>
          <span class="st-task-meta-sep" />
          <span class="st-task-meta-item" classList={{ "st-task-meta-item--next": active() }}>
            <StIcon name={active() ? "clock" : "pause"} size={12} />
            {active() ? `下次 ${relativeTime(t.next_run_at)}` : "已暂停，不再调度"}
          </span>
          <span class="st-task-meta-sep" />
          <span class="st-task-meta-item st-task-meta-item--dir" title={t.directory}>
            <StIcon name="folder" size={12} />
            {t.directory}
          </span>
        </div>
      </div>

      <div class="st-task-side">
        <Show when={!active()}>
          <span class="st-task-status-text">已暂停</span>
        </Show>
        <div class="st-task-actions">
          <button
            type="button"
            class="st-icon-btn"
            title="立即运行一次"
            aria-label="立即运行一次"
            onClick={(e) => {
              e.stopPropagation()
              c.runNow(t)
            }}
          >
            <StIcon name="play" size={14} />
          </button>
          <button
            type="button"
            class="st-icon-btn"
            title="编辑任务"
            aria-label="编辑任务"
            onClick={(e) => {
              e.stopPropagation()
              c.openEdit(t)
            }}
          >
            <StIcon name="edit" size={14} />
          </button>
          <button
            type="button"
            class="st-icon-btn st-icon-btn--danger"
            title="删除任务"
            aria-label="删除任务"
            onClick={(e) => {
              e.stopPropagation()
              c.setConfirmDeleteId(t.id)
            }}
          >
            <StIcon name="trash" size={14} />
          </button>
        </div>
        <Switch on={active()} onToggle={() => c.toggleStatus(t)} />
      </div>
    </article>
  )
}

// ===== 概览（KPI + 未来 7 天节奏）=====
function Overview(props: { c: ScheduledTasksController }) {
  const c = props.c

  const counts = createMemo(() => {
    const all = c.allTasks()
    return {
      active: all.filter((t) => t.status === "active").length,
      paused: all.filter((t) => t.status !== "active").length,
      total: all.length,
    }
  })

  const successRate = createMemo(() => {
    const done = c.allHistory().filter((r) => r.status !== "running")
    if (done.length === 0) return "—"
    const ok = done.filter((r) => r.status === "success").length
    return `${Math.round((ok / done.length) * 100)}%`
  })

  const rhythm = createMemo(() => {
    const today = startOfDay(new Date())
    const days = Array.from({ length: 7 }, (_, i) => {
      const date = new Date(today)
      date.setDate(date.getDate() + i)
      return {
        date,
        label: i === 0 ? "今天" : i === 1 ? "明天" : `周${WEEKDAY[date.getDay()]}`,
        count: 0,
      }
    })
    for (const task of c.allTasks()) {
      if (task.status !== "active") continue
      for (const day of days) if (runsOn(task, day.date)) day.count++
    }
    return days
  })

  const weekTotal = createMemo(() => rhythm().reduce((sum, d) => sum + d.count, 0))

  return (
    <section class="st-overview">
      <div class="st-kpis">
        <div class="st-kpi">
          <span class="st-kpi-value">{counts().active}</span>
          <span class="st-kpi-label">运行中</span>
        </div>
        <span class="st-kpi-sep" />
        <div class="st-kpi">
          <span class="st-kpi-value st-kpi-value--muted">{counts().paused}</span>
          <span class="st-kpi-label">已暂停</span>
        </div>
        <span class="st-kpi-sep" />
        <div class="st-kpi">
          <span class="st-kpi-value">{weekTotal()}</span>
          <span class="st-kpi-label">未来 7 天执行</span>
        </div>
        <span class="st-kpi-sep" />
        <div class="st-kpi">
          <span class="st-kpi-value">{successRate()}</span>
          <span class="st-kpi-label">历史成功率</span>
        </div>
      </div>

      <div class="st-rhythm" aria-label="未来 7 天执行节奏">
        <For each={rhythm()}>
          {(day) => (
            <div class="st-rhythm-col" classList={{ "st-rhythm-col--today": day.label === "今天" }}>
              <div class="st-rhythm-stack" title={`${day.label}：${day.count} 次执行`}>
                <For each={Array.from({ length: Math.min(day.count, 4) })}>{() => <i class="st-rhythm-dot" />}</For>
                <Show when={day.count > 4}>
                  <span class="st-rhythm-more">+{day.count - 4}</span>
                </Show>
              </div>
              <span class="st-rhythm-day">{day.label}</span>
            </div>
          )}
        </For>
      </div>
    </section>
  )
}

// ===== 任务列表页 =====
export function ScheduledTasksView(props: { controller: ScheduledTasksController }) {
  const c = props.controller
  const counts = createMemo(() => {
    const all = c.allTasks()
    return {
      all: all.length,
      active: all.filter((t) => t.status === "active").length,
      paused: all.filter((t) => t.status !== "active").length,
    }
  })

  return (
    <div class="st-page">
      <header class="st-hero">
        <div class="st-hero-text">
          <h1 class="st-title">定时任务</h1>
          <p class="st-subtitle">按计划自动执行安全任务，结果沉淀为可追溯的运行记录</p>
        </div>
        <button class="st-btn-primary" onClick={c.openCreate}>
          <StIcon name="plus" size={14} />
          新建任务
        </button>
      </header>

      <Overview c={c} />

      <div class="st-toolbar">
        <div class="st-seg">
          <button
            class="st-seg-item"
            classList={{ "st-seg-item--active": c.statusFilter() === "all" }}
            onClick={() => c.setStatusFilter("all")}
          >
            全部<span class="st-seg-count">{counts().all}</span>
          </button>
          <button
            class="st-seg-item"
            classList={{ "st-seg-item--active": c.statusFilter() === "active" }}
            onClick={() => c.setStatusFilter("active")}
          >
            运行中<span class="st-seg-count">{counts().active}</span>
          </button>
          <button
            class="st-seg-item"
            classList={{ "st-seg-item--active": c.statusFilter() === "paused" }}
            onClick={() => c.setStatusFilter("paused")}
          >
            已暂停<span class="st-seg-count">{counts().paused}</span>
          </button>
        </div>

        <label class="st-search">
          <StIcon name="search" size={14} />
          <input
            type="text"
            placeholder="搜索任务名称或内容"
            value={c.searchQuery()}
            onInput={(e) => c.setSearchQuery(e.currentTarget.value)}
          />
        </label>
      </div>

      <Show
        when={c.tasks().length > 0}
        fallback={
          <EmptyState
            icon={c.allTasks().length === 0 ? "spark" : "search"}
            title={c.allTasks().length === 0 ? "还没有定时任务" : "没有匹配的任务"}
            desc={
              c.allTasks().length === 0
                ? "创建一个任务，让安全巡检、情报汇总按节奏自动跑起来"
                : "换个关键词或切换状态筛选试试"
            }
            action={c.allTasks().length === 0 ? c.openCreate : undefined}
          />
        }
      >
        <div class="st-list">
          <For each={c.tasks()}>{(task) => <TaskRow c={c} task={task} />}</For>
        </div>
      </Show>

      <Show when={c.showForm()}>
        <TaskForm c={c} />
      </Show>

      <ConfirmDelete c={c} />
    </div>
  )
}

// ===== 运行记录 =====
function groupByDay(records: ExecutionRecord[]) {
  const groups = new Map<string, ExecutionRecord[]>()
  for (const record of records) {
    const key = startOfDay(new Date(record.time_started)).getTime().toString()
    const bucket = groups.get(key)
    if (bucket) bucket.push(record)
    else groups.set(key, [record])
  }
  return [...groups.entries()].map(([key, items]) => ({
    key,
    label: dayLabel(new Date(Number(key))),
    date: new Date(Number(key)),
    items,
  }))
}

export function ScheduledTasksHistoryView(props: { controller: ScheduledTasksController }) {
  const c = props.controller

  const stats = createMemo(() => {
    const all = c.allHistory()
    const done = all.filter((r) => r.status !== "running")
    const ok = done.filter((r) => r.status === "success").length
    const failed = done.filter((r) => r.status === "failed").length
    const durations = done
      .map((r) => (r.time_finished ? r.time_finished - r.time_started : undefined))
      .filter((v): v is number => typeof v === "number")
    const avg = durations.length ? durations.reduce((a, b) => a + b, 0) / durations.length : 0
    return {
      total: all.length,
      ok,
      failed,
      rate: done.length ? `${Math.round((ok / done.length) * 100)}%` : "—",
      avg: c.formatDuration(avg),
    }
  })

  const groups = createMemo(() => groupByDay(c.history()))

  const filterCounts = createMemo(() => {
    const all = c.allHistory()
    return {
      all: all.length,
      success: all.filter((r) => r.status === "success").length,
      failed: all.filter((r) => r.status === "failed").length,
      running: all.filter((r) => r.status === "running").length,
    }
  })

  return (
    <div class="st-page">
      <header class="st-hero">
        <div class="st-hero-text">
          <h1 class="st-title">运行记录</h1>
          <p class="st-subtitle">每一次调度与手动执行的完整留痕</p>
        </div>
        <div class="st-hero-stats">
          <div class="st-hero-stat">
            <span class="st-hero-stat-value">{stats().total}</span>
            <span class="st-hero-stat-label">总执行</span>
          </div>
          <span class="st-kpi-sep" />
          <div class="st-hero-stat">
            <span class="st-hero-stat-value">{stats().rate}</span>
            <span class="st-hero-stat-label">成功率</span>
          </div>
          <span class="st-kpi-sep" />
          <div class="st-hero-stat">
            <span class="st-hero-stat-value">{stats().avg}</span>
            <span class="st-hero-stat-label">平均耗时</span>
          </div>
        </div>
      </header>

      <div class="st-toolbar">
        <div class="st-seg">
          <button
            class="st-seg-item"
            classList={{ "st-seg-item--active": c.historyFilter() === "all" }}
            onClick={() => c.setHistoryFilter("all")}
          >
            全部<span class="st-seg-count">{filterCounts().all}</span>
          </button>
          <button
            class="st-seg-item"
            classList={{ "st-seg-item--active": c.historyFilter() === "success" }}
            onClick={() => c.setHistoryFilter("success")}
          >
            成功<span class="st-seg-count">{filterCounts().success}</span>
          </button>
          <button
            class="st-seg-item"
            classList={{ "st-seg-item--active": c.historyFilter() === "failed" }}
            onClick={() => c.setHistoryFilter("failed")}
          >
            失败<span class="st-seg-count">{filterCounts().failed}</span>
          </button>
          <button
            class="st-seg-item"
            classList={{ "st-seg-item--active": c.historyFilter() === "running" }}
            onClick={() => c.setHistoryFilter("running")}
          >
            执行中<span class="st-seg-count">{filterCounts().running}</span>
          </button>
        </div>

        <label class="st-search">
          <StIcon name="search" size={14} />
          <input
            type="text"
            placeholder="搜索任务名称"
            value={c.historySearch()}
            onInput={(e) => c.setHistorySearch(e.currentTarget.value)}
          />
        </label>
      </div>

      <Show
        when={c.history().length > 0}
        fallback={
          <EmptyState icon="history" title="暂无执行记录" desc="任务首次触发后，这里会按天归档每次执行的结果" />
        }
      >
        <div class="st-log">
          <For each={groups()}>
          {(group) => (
            <section class="st-log-group">
              <div class="st-log-date">
                <span>{group.label}</span>
                <span class="st-log-date-count">{group.items.length} 次</span>
              </div>
              <div class="st-log-items">
                <For each={group.items}>
                  {(record) => {
                    const duration = record.time_finished ? record.time_finished - record.time_started : undefined
                    return (
                      <div class="st-log-item">
                        <span class="st-log-time">
                          {pad(new Date(record.time_started).getHours())}:
                          {pad(new Date(record.time_started).getMinutes())}
                        </span>
                        <span class="st-log-rail">
                          <i class="st-log-node" data-status={record.status} />
                        </span>
                        <div class="st-log-body">
                          <div class="st-log-head">
                            <span class="st-log-name">{c.getTaskName(record.task_id)}</span>
                            <StatusBadge status={record.status} label={c.statusLabels[record.status]} />
                            <span class="st-log-trigger">
                              {record.trigger_type === "manual" ? "手动触发" : "定时触发"}
                            </span>
                          </div>
                          <p class="st-log-msg" classList={{ "st-log-msg--error": record.status === "failed" }}>
                            {record.status === "running" ? "执行中…" : (record.error_message ?? "执行完成，未发现异常")}
                          </p>
                        </div>
                        <span class="st-log-duration">{c.formatDuration(duration)}</span>
                        <button class="st-log-detail" onClick={() => c.setSelectedHistoryId(record.id)}>
                          详情
                        </button>
                      </div>
                    )
                  }}
                </For>
              </div>
            </section>
          )}
        </For>
        </div>
      </Show>

      <HistoryDetail c={c} />
    </div>
  )
}

// ===== 新建 / 编辑 =====
function previewNextRun(f: TaskFormState): string {
  const [hours, minutes] = (f.schedule_time || "09:00").split(":").map((v) => Number(v) || 0)
  const now = new Date()

  if (f.schedule_type === "daily") {
    const next = new Date(now)
    next.setHours(hours, minutes, 0, 0)
    if (next <= now) next.setDate(next.getDate() + 1)
    return `${dayLabel(next)} ${pad(hours)}:${pad(minutes)}`
  }
  if (f.schedule_type === "weekly") {
    const next = new Date(now)
    next.setHours(hours, minutes, 0, 0)
    const offset = ((f.schedule_weekday ?? 1) - next.getDay() + 7) % 7
    next.setDate(next.getDate() + offset)
    if (next <= now) next.setDate(next.getDate() + 7)
    return `${dayLabel(next)} 周${WEEKDAY[next.getDay()]} ${pad(hours)}:${pad(minutes)}`
  }
  if (f.schedule_type === "monthly") {
    const next = new Date(now)
    next.setHours(hours, minutes, 0, 0)
    next.setDate(f.schedule_month_day ?? 1)
    if (next <= now) next.setMonth(next.getMonth() + 1)
    return `${next.getMonth() + 1} 月 ${next.getDate()} 日 ${pad(hours)}:${pad(minutes)}`
  }
  if (!f.scheduled_at) return "未设置执行时间"
  const at = new Date(f.scheduled_at)
  if (Number.isNaN(at.getTime())) return "未设置执行时间"
  return `${at.getMonth() + 1} 月 ${at.getDate()} 日 ${pad(at.getHours())}:${pad(at.getMinutes())}`
}

const SCHEDULE_TYPES: { value: TaskFormState["schedule_type"]; label: string; icon: StIconName }[] = [
  { value: "once", label: "单次", icon: "bolt" },
  { value: "daily", label: "每天", icon: "clock" },
  { value: "weekly", label: "每周", icon: "week" },
  { value: "monthly", label: "每月", icon: "month" },
]

function TaskForm(props: { c: ScheduledTasksController }) {
  const c = props.c
  const isValid = () => c.formData.name.trim().length > 0 && c.formData.prompt.trim().length > 0

  return (
    <div class="st-modal-overlay" onClick={c.closeForm}>
      <div class="st-modal" onClick={(e) => e.stopPropagation()}>
        <header class="st-modal-head">
          <div class="st-modal-head-text">
            <h2 class="st-modal-title">{c.editingTaskId() ? "编辑定时任务" : "新建定时任务"}</h2>
            <p class="st-modal-desc">描述要做的事，选一个节奏，剩下的交给调度器</p>
          </div>
          <button class="st-icon-btn" aria-label="关闭" onClick={c.closeForm}>
            <StIcon name="close" size={15} />
          </button>
        </header>

        <div class="st-modal-body">
          <section class="st-section">
            <div class="st-section-title">任务内容</div>

            <div class="st-field">
              <label class="st-field-label">任务名称</label>
              <input
                class="st-input"
                placeholder="如：每日安全资讯摘要"
                value={c.formData.name}
                onInput={(e) => c.setFormData("name", e.currentTarget.value)}
              />
            </div>

            <div class="st-field">
              <label class="st-field-label">
                提示词
                <span class="st-field-required">必填</span>
              </label>
              <textarea
                class="st-textarea"
                placeholder="描述任务执行时需要完成的工作，如：汇总今日安全资讯，生成报告"
                value={c.formData.prompt}
                onInput={(e) => c.setFormData("prompt", e.currentTarget.value)}
              />
              <span class="st-field-hint">调度触发时会把这句提示词交给 Agent 执行</span>
            </div>

            <div class="st-field">
              <label class="st-field-label">备注</label>
              <input
                class="st-input"
                placeholder="可选，用于说明任务目的"
                value={c.formData.description}
                onInput={(e) => c.setFormData("description", e.currentTarget.value)}
              />
            </div>
          </section>

          <section class="st-section">
            <div class="st-section-title">调度规则</div>

            <div class="st-cadence-group">
              <For each={SCHEDULE_TYPES}>
                {(type) => (
                  <button
                    type="button"
                    class="st-cadence-option"
                    classList={{ "st-cadence-option--active": c.formData.schedule_type === type.value }}
                    onClick={() => c.setFormData("schedule_type", type.value)}
                  >
                    <StIcon name={type.icon} size={15} />
                    {type.label}
                  </button>
                )}
              </For>
            </div>

            <div class="st-form-grid">
              <Show when={c.formData.schedule_type !== "once"}>
                <div class="st-field">
                  <label class="st-field-label">执行时间</label>
                  <input
                    class="st-input"
                    type="time"
                    value={c.formData.schedule_time}
                    onInput={(e) => c.setFormData("schedule_time", e.currentTarget.value)}
                  />
                </div>
              </Show>

              <Show when={c.formData.schedule_type === "weekly"}>
                <div class="st-field">
                  <label class="st-field-label">星期</label>
                  <select
                    class="st-input"
                    value={c.formData.schedule_weekday}
                    onChange={(e) => c.setFormData("schedule_weekday", Number(e.currentTarget.value))}
                  >
                    <For each={c.weekdayLabels}>{(label, i) => <option value={i()}>{label}</option>}</For>
                  </select>
                </div>
              </Show>

              <Show when={c.formData.schedule_type === "monthly"}>
                <div class="st-field">
                  <label class="st-field-label">日期</label>
                  <select
                    class="st-input"
                    value={c.formData.schedule_month_day}
                    onChange={(e) => c.setFormData("schedule_month_day", Number(e.currentTarget.value))}
                  >
                    <For each={Array.from({ length: 28 }, (_, i) => i + 1)}>
                      {(d) => <option value={d}>{d} 日</option>}
                    </For>
                  </select>
                </div>
              </Show>

              <Show when={c.formData.schedule_type === "once"}>
                <div class="st-field">
                  <label class="st-field-label">执行时间</label>
                  <input
                    class="st-input"
                    type="datetime-local"
                    value={c.formData.scheduled_at}
                    onInput={(e) => c.setFormData("scheduled_at", e.currentTarget.value)}
                  />
                </div>
              </Show>
            </div>

            <div class="st-preview">
              <StIcon name="clock" size={14} />
              <span class="st-preview-label">首次执行</span>
              <span class="st-preview-value">{previewNextRun(c.formData)}</span>
            </div>
          </section>

          <section class="st-section">
            <div class="st-section-title">运行配置</div>
            <div class="st-form-grid">
              <div class="st-field">
                <label class="st-field-label">工作目录</label>
                <input
                  class="st-input"
                  placeholder="/tmp 或项目路径"
                  value={c.formData.directory}
                  onInput={(e) => c.setFormData("directory", e.currentTarget.value)}
                />
              </div>
              <div class="st-field">
                <label class="st-field-label">Agent</label>
                <input
                  class="st-input"
                  placeholder="默认 agent"
                  value={c.formData.agent}
                  onInput={(e) => c.setFormData("agent", e.currentTarget.value)}
                />
              </div>
            </div>
          </section>

          <Show when={c.error()}>
            <div class="st-form-error">
              <StIcon name="alert" size={14} />
              {c.error()}
            </div>
          </Show>
        </div>

        <footer class="st-modal-foot">
          <button class="st-btn-ghost" onClick={c.closeForm}>
            取消
          </button>
          <button class="st-btn-primary" disabled={!isValid() || c.loading()} onClick={c.saveForm}>
            {c.loading() ? "保存中…" : c.editingTaskId() ? "保存修改" : "创建任务"}
          </button>
        </footer>
      </div>
    </div>
  )
}

// ===== 删除确认 =====
function ConfirmDelete(props: { c: ScheduledTasksController }) {
  const c = props.c
  const task = () => c.allTasks().find((t) => t.id === c.confirmDeleteId())

  return (
    <Show when={c.confirmDeleteId()}>
      <div class="st-modal-overlay st-modal-overlay--center" onClick={() => c.setConfirmDeleteId(null)}>
        <div class="st-confirm" onClick={(e) => e.stopPropagation()}>
          <div class="st-confirm-icon">
            <StIcon name="trash" size={18} />
          </div>
          <h2 class="st-confirm-title">删除任务</h2>
          <p class="st-confirm-text">
            确认删除「{task()?.name ?? ""}」？该任务的运行记录会一并移除，操作不可撤销。
          </p>
          <div class="st-confirm-actions">
            <button class="st-btn-ghost" onClick={() => c.setConfirmDeleteId(null)}>
              取消
            </button>
            <button
              class="st-btn-danger"
              onClick={() => {
                c.deleteTask(c.confirmDeleteId()!)
                c.setConfirmDeleteId(null)
              }}
            >
              确认删除
            </button>
          </div>
        </div>
      </div>
    </Show>
  )
}

// ===== 执行详情抽屉 =====
function HistoryDetail(props: { c: ScheduledTasksController }) {
  const c = props.c
  const record = c.selectedHistory

  return (
    <Show when={c.selectedHistoryId()}>
      <div class="st-drawer-overlay" onClick={() => c.setSelectedHistoryId(null)}>
        <aside class="st-drawer" onClick={(e) => e.stopPropagation()}>
          <header class="st-drawer-head">
            <div>
              <h2 class="st-modal-title">执行详情</h2>
              <p class="st-modal-desc">{record() ? c.getTaskName(record()!.task_id) : ""}</p>
            </div>
            <button class="st-icon-btn" aria-label="关闭" onClick={() => c.setSelectedHistoryId(null)}>
              <StIcon name="close" size={15} />
            </button>
          </header>

          <Show when={record()}>
            {(r) => {
              const task = () => c.allTasks().find((t) => t.id === r().task_id)
              const duration = () => {
                const rec = r()
                if (!rec?.time_finished) return undefined
                return rec.time_finished - rec.time_started
              }
              return (
                <div class="st-drawer-body">
                  <div class="st-drawer-hero">
                    <span class="st-drawer-hero-status" data-status={r().status}>
                      <i class="st-badge-dot" />
                      {c.statusLabels[r().status]}
                    </span>
                    <span class="st-drawer-hero-duration">{c.formatDuration(duration())}</span>
                  </div>

                  <div class="st-drawer-grid">
                    <div class="st-meta-cell">
                      <span class="st-field-label">触发方式</span>
                      <span class="st-meta-value">{r().trigger_type === "manual" ? "手动触发" : "定时触发"}</span>
                    </div>
                    <div class="st-meta-cell">
                      <span class="st-field-label">开始时间</span>
                      <span class="st-meta-value">{c.formatTime(r().time_started)}</span>
                    </div>
                    <div class="st-meta-cell">
                      <span class="st-field-label">结束时间</span>
                      <span class="st-meta-value">{c.formatTime(r().time_finished)}</span>
                    </div>
                    <div class="st-meta-cell">
                      <span class="st-field-label">调度规则</span>
                      <span class="st-meta-value">{task() ? c.scheduleLabel(task()!) : "—"}</span>
                    </div>
                  </div>

                  <Show when={task()?.prompt}>
                    <div class="st-drawer-section">
                      <span class="st-field-label">执行提示词</span>
                      <pre class="st-code">{task()?.prompt}</pre>
                    </div>
                  </Show>

                  <div class="st-drawer-section">
                    <span class="st-field-label">执行结果</span>
                    <Show
                      when={r().error_message}
                      fallback={<p class="st-drawer-ok">执行完成，未发现异常</p>}
                    >
                      <pre class="st-code st-code--error">{r().error_message}</pre>
                    </Show>
                  </div>
                </div>
              )
            }}
          </Show>
        </aside>
      </div>
    </Show>
  )
}
